import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DateRangeFilter,
  EMPTY_DATE_RANGE,
  PageContainer,
  SelectFilter,
  type DateRangeValue,
  type FilterOption,
} from "@outpave/ds";
import { Inline } from "components/layout/reusableLayouts";
import AccountFilterButton, {
  ALL_ACCOUNTS_ID,
} from "components/admin/transactions/AccountFilterButton";
import AmountRangeChip, {
  type AmountRange,
} from "components/common/AmountRangeChip";
import TransactionDetailsDrawer from "components/common/transactions/TransactionDetailsDrawer";
import TransactionsTable, {
  type TransactionFilterCriteria,
} from "components/common/transactions/TransactionsTable";
import AccountType from "enums/AccountType";
import Role from "enums/Role";
import TransactionStatus from "enums/TransactionStatus";
import type TransactionsType from "enums/TransactionsType";
import ViolationType from "enums/ViolationType";
import useDebouncedSearch from "hooks/useDebouncedSearch";
import useGetBankAccounts from "hooks/useGetBankAccounts";
import useGetParamFromUrl from "hooks/useGetParamFromUrl";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { movesFunds } from "utils/accounts/moneyMovement";
import {
  useDateRangeFilterLabels,
  useDateRangePresets,
} from "utils/dateRangeFilter";
import {
  CARD_SPEND_STATUSES,
  CARD_TYPES,
  CLOSED_REQUEST_STATUSES,
  COMPLIANCE_LABELS,
  COMPLIANCE_TYPES,
  LEDGER_TYPES,
  PERSONAL_SPEND_STATUSES,
  TASK_COUNT_TYPES,
  TASKS_COMPLIANCES,
  TASKS_TYPES,
  TRANSFER_STATUSES,
  TRANSFER_TYPES,
  TYPE_FILTER_GROUPS,
} from "utils/transactions/transactionGroups";
import {
  MY_TRANSACTIONS_LINK_PARAMS,
  readMyTransactionsLink,
} from "utils/transactions/myTransactionsLink";
import {
  statusFilter,
  statusFilterLabels,
} from "utils/transactions/statusFilter";

type TransactionTab = {
  key: string;
  label: string;
  status: TransactionStatus[];
  type: TransactionsType[];
  /** A compliance list's own constraint, which applies while no Compliance option is picked. */
  compliances?: ViolationType[];
};

const MY_STATUSES = [...CARD_SPEND_STATUSES, ...PERSONAL_SPEND_STATUSES];

const MY_TABS: TransactionTab[] = [
  { key: "all", label: "Show All", status: MY_STATUSES, type: CARD_TYPES },
  {
    key: "task",
    label: "Task",
    status: MY_STATUSES.filter(
      (status) => !CLOSED_REQUEST_STATUSES.includes(status),
    ),
    type: TASK_COUNT_TYPES,
    compliances: TASKS_COMPLIANCES,
  },
];

const LEDGER_TABS: TransactionTab[] = [
  {
    key: "all",
    label: "Show All",
    // Every status: the payment and transfer lifecycles reach statuses card spend never does.
    status: Object.values(TransactionStatus),
    type: LEDGER_TYPES,
  },
  {
    key: "needs-review",
    label: "Needs Review",
    status: [TransactionStatus.COMPLETE, TransactionStatus.PENDING],
    type: TASKS_TYPES,
    compliances: TASKS_COMPLIANCES,
  },
];

const TRANSFERS_TAB: TransactionTab = {
  key: "transfer-history",
  label: "Transfers",
  status: TRANSFER_STATUSES,
  type: TRANSFER_TYPES,
};

const EMPTY_AMOUNT_RANGE: AmountRange = {};

/** The picks this tab can show; the rest stay picked for a tab that shows them. */
const onTab = (picked: string[], offered: readonly FilterOption[]) =>
  picked.filter((value) => offered.some((option) => option.value === value));

interface TransactionsPageProps {
  /** Only the viewer's own transactions, whatever their role. */
  mine?: boolean;
}

const TransactionsPage: React.FC<TransactionsPageProps> = ({
  mine = false,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // The Account filter and the Transfers tab rest on `getListBankAccounts`, which the server gates at Auditor.
  const isAuditorOrHigher = useCheckUserRole(Role.Auditor);
  const accountTools = !mine && isAuditorOrHigher;

  const [activeTabKey, setActiveTabKey] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const search = useDebouncedSearch(setSearchTerm);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedTypeGroups, setSelectedTypeGroups] = useState<string[]>([]);
  const [selectedCompliances, setSelectedCompliances] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<DateRangeValue>(EMPTY_DATE_RANGE);
  const datePresets = useDateRangePresets();
  const dateLabels = useDateRangeFilterLabels();
  const [amountRange, setAmountRange] =
    useState<AmountRange>(EMPTY_AMOUNT_RANGE);
  const [selectedAccountId, setSelectedAccountId] = useState(
    (location.state as { accountId?: string } | null)?.accountId ??
      ALL_ACCOUNTS_ID,
  );
  const [openTransactionId, setOpenTransactionId] = useState<string | null>(
    null,
  );
  const [refreshIndex, setRefreshIndex] = useState(0);

  // /my/transactions?id=<txId> and /admin/transactions?id=<txId> open that transaction.
  const resetParam = useGetParamFromUrl("id", setOpenTransactionId);

  // A dashboard link preselects the tab and filters: apply it once and drop it, so the address never contradicts the chips.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!MY_TRANSACTIONS_LINK_PARAMS.some((param) => params.has(param))) return;
    const link = readMyTransactionsLink(params);
    if (link.tab) setActiveTabKey(link.tab);
    if (link.statuses.length > 0)
      setSelectedStatuses(statusFilterLabels(link.statuses));
    if (link.compliance) setSelectedCompliances([link.compliance]);
    MY_TRANSACTIONS_LINK_PARAMS.forEach((param) => params.delete(param));
    const rest = params.toString();
    navigate(`${location.pathname}${rest ? `?${rest}` : ""}`, {
      replace: true,
    });
  }, [location.pathname, location.search, navigate]);

  const { allAccounts } = useGetBankAccounts({
    accountTypes: [
      AccountType.FINANCIAL_ACCOUNT,
      AccountType.PLAID_EXTERNAL_CARD_ACCOUNT,
    ],
    withBalances: false,
    skip: !accountTools,
  });

  // CHARGE programs have no deposit or withdraw flows, so a company whose programs are all CHARGE gets no Transfers tab.
  const programs = allAccounts.filter(
    (account) => account.accountType === AccountType.FINANCIAL_ACCOUNT,
  );
  const onlyChargePrograms = programs.length > 0 && !programs.some(movesFunds);

  const tabs = useMemo(() => {
    if (mine) return MY_TABS;
    return accountTools && !onlyChargePrograms
      ? [...LEDGER_TABS, TRANSFERS_TAB]
      : LEDGER_TABS;
  }, [mine, accountTools, onlyChargePrograms]);

  // Keyed rather than indexed: the tabs shrink once the accounts query resolves on a CHARGE company.
  const activeTab = tabs.find((tab) => tab.key === activeTabKey) ?? tabs[0];

  const statusChoices = useMemo(
    () => statusFilter(activeTab.status, (key) => t(key)),
    [activeTab, t],
  );

  const typeOptions: FilterOption[] = useMemo(
    () =>
      TYPE_FILTER_GROUPS.filter((group) =>
        group.types.some((type) => activeTab.type.includes(type)),
      ).map((group) => ({ value: group.id, label: t(group.label) })),
    [activeTab, t],
  );

  // Money movement never owes compliance, so a tab listing only that has no Compliance filter.
  const offersCompliance = activeTab.type.some((type) =>
    COMPLIANCE_TYPES.includes(type),
  );

  const complianceOptions: FilterOption[] = useMemo(
    () =>
      (activeTab.compliances ?? TASKS_COMPLIANCES).map((compliance) => ({
        value: compliance,
        label: t(COMPLIANCE_LABELS[compliance]),
      })),
    [activeTab, t],
  );

  const statusPicks = useMemo(
    () =>
      onTab(
        selectedStatuses,
        statusChoices.options.flatMap((group) => group.options),
      ),
    [selectedStatuses, statusChoices],
  );
  const typePicks = useMemo(
    () => onTab(selectedTypeGroups, typeOptions),
    [selectedTypeGroups, typeOptions],
  );
  const compliancePicks = useMemo(
    () =>
      offersCompliance ? onTab(selectedCompliances, complianceOptions) : [],
    [offersCompliance, selectedCompliances, complianceOptions],
  );

  const filterCriteria: TransactionFilterCriteria = useMemo(() => {
    const pickedTypes = TYPE_FILTER_GROUPS.filter((group) =>
      typePicks.includes(group.id),
    ).flatMap((group) =>
      group.types.filter((type) => activeTab.type.includes(type)),
    );
    return {
      status:
        statusPicks.length > 0
          ? statusChoices.statusesFor(statusPicks)
          : activeTab.status,
      type: pickedTypes.length > 0 ? pickedTypes : activeTab.type,
      compliances:
        compliancePicks.length > 0
          ? (compliancePicks as ViolationType[])
          : activeTab.compliances,
      searchTerm,
      dateRange,
      amountRange,
      // Only for a viewer who also sees the Account filter, so nobody is scoped by a chip they cannot see or clear.
      accountId:
        accountTools && selectedAccountId !== ALL_ACCOUNTS_ID
          ? selectedAccountId
          : null,
    };
  }, [
    activeTab,
    statusPicks,
    statusChoices,
    typePicks,
    compliancePicks,
    searchTerm,
    dateRange,
    amountRange,
    accountTools,
    selectedAccountId,
  ]);

  return (
    <PageContainer
      title={t("Transactions")}
      secondaryAction={
        accountTools ? (
          <AccountFilterButton
            accounts={allAccounts}
            value={selectedAccountId}
            onChange={setSelectedAccountId}
          />
        ) : undefined
      }
      tabs={{
        items: tabs.map((tab) => ({ id: tab.key, label: t(tab.label) })),
        activeId: activeTab.key,
        onChange: setActiveTabKey,
      }}
      search={{ ...search, placeholder: t("Search") }}
      filters={
        <Inline gap="var(--space-3)" align="center">
          <DateRangeFilter
            label={t("Date range")}
            presets={datePresets}
            labels={dateLabels}
            value={dateRange}
            onChange={setDateRange}
          />
          <AmountRangeChip
            label={t("Amount")}
            value={amountRange}
            onChange={setAmountRange}
          />
          <SelectFilter
            multiple
            label={t("Type")}
            options={typeOptions}
            selected={typePicks}
            onChange={setSelectedTypeGroups}
          />
          <SelectFilter
            multiple
            label={t("Status")}
            options={statusChoices.options}
            selected={statusPicks}
            onChange={setSelectedStatuses}
          />
          {offersCompliance && (
            <SelectFilter
              multiple
              label={t("Compliance")}
              options={complianceOptions}
              selected={compliancePicks}
              onChange={setSelectedCompliances}
            />
          )}
        </Inline>
      }
    >
      <TransactionsTable
        mine={mine}
        filterCriteria={filterCriteria}
        onRowClick={setOpenTransactionId}
        refreshTransactions={refreshIndex}
      />
      <TransactionDetailsDrawer
        open={openTransactionId !== null}
        transactionId={openTransactionId ?? ""}
        handleCloseDrawer={() => {
          setOpenTransactionId(null);
          resetParam();
        }}
        handleRefreshTransactions={() => setRefreshIndex((i) => i + 1)}
      />
    </PageContainer>
  );
};

export default TransactionsPage;
